<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <AppFormWrapper id="email-new-form" @submit.prevent="emit('submit')">
    <!-- one line at every width: the largest size that fits (useMaxFontSize) -->
    <h2 ref="titleRef">{{ copy.heading }}</h2>
    <p>{{ copy.text }}</p>
    <input
      v-model="newEmail"
      type="email"
      :placeholder="copy.placeholder || 'Enter your email address'"
      :autocomplete="purpose === 'assistant' ? 'off' : 'email'"
      :aria-label="copy.placeholder || 'Your email address'"
      required />
    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
  </AppFormWrapper>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {useRoute} from "vue-router";
import AppFormWrapper from "@/components/App/Form/Wrapper";
import {useEmailChange} from "@/composables/useEmailChange";
import {useMaxFontSize} from "@/composables/useMaxFontSize";

const props = defineProps({
  errorMessage: {type: String, default: ""},
  purpose: {type: String, default: "replace"}, // "replace", "secondary" or "assistant"
  hasAssistant: {type: Boolean, default: false}, // assistant: Change, not Add
});
const emit = defineEmits(["submit", "update:canSubmit"]);

const COPY = {
  // Seán, 2026-10-23 (issue DL3)
  replace: {heading: "Change Email", text: "Enter your new email address.",
    placeholder: "New email address"},
  secondary: {
    heading: "Add Secondary Email",
    text: "Add another email address you can use to sign in.",
  },
  // another person's account, linked (no code: they don't sign in as you); the same words as
  // the email editor's "?" (Profile/EmailEditor.vue)
  assistant: {
    heading: "Add Assistant",
    text: "We'll send your assistant a copy of the emails we send you. They can't sign in to "
      + "your account.",
    placeholder: "Your assistant's email address",
  },
};

const copy = computed(() => {
  const base = COPY[props.purpose];
  if (props.purpose !== "assistant" || !props.hasAssistant) return base;
  return {...base, heading: "Change Assistant"};
});

const {newEmail} = useEmailChange();

const route = useRoute();
const titleRef = ref(null);
// the title on one line, as large as fits (the dialog's own h2 size at most); refit as it opens
const isOpen = () => route.hash.startsWith("#new-email");
useMaxFontSize(titleRef, {maxFontSize: 48, fillsWidth: true, isActive: isOpen});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const canSubmit = computed(() => emailRegex.test(newEmail.value));
watch(canSubmit, (val) => emit("update:canSubmit", val), {immediate: true});
</script>

<style scoped>
/* no width of its own, so its one line never widens the dialog: useMaxFontSize fits it to the
   width the dialog has */
h2 {
  width: 0;
  min-width: 100%;
}
</style>
